/* eslint-disable no-undef */
import type { MapPalette, MapStyleTokens } from './map.types';
import { MAP_STYLE_VALUES } from './map.types';

/** Google Maps expects `#RRGGBB`; normalises shorthand hex and drops unsafe values. */
export const mapStylerColor = (
  value: string | undefined,
  fallback: string,
): string => {
  if (!value || typeof value !== 'string') return fallback;
  const hex = value.trim();
  if (!hex.startsWith('#') || hex.length < 4) return fallback;
  if (hex.length === 4) {
    return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`.toLowerCase();
  }
  return hex.length === 7 ? hex.toLowerCase() : fallback;
};

export const HIDE_INTERESTING_POINTS_STYLES: google.maps.MapTypeStyle[] = [
  { featureType: 'poi.attraction', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.government', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.medical', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', stylers: [{ visibility: 'off' }] },
  {
    featureType: 'poi.place_of_worship',
    stylers: [{ visibility: 'off' }],
  },
  { featureType: 'poi.school', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.sports_complex', stylers: [{ visibility: 'off' }] },
];

export const buildMapStyles = (
  palette: MapPalette,
): google.maps.MapTypeStyle[] => [
  {
    elementType: 'geometry',
    stylers: [{ color: mapStylerColor(palette.background, '#f5f5f5') }],
  },
  {
    featureType: 'landscape',
    elementType: 'geometry',
    stylers: [{ color: mapStylerColor(palette.background, '#f5f5f5') }],
  },
  {
    featureType: 'landscape.natural',
    elementType: 'geometry',
    stylers: [{ color: mapStylerColor(palette.background, '#f5f5f5') }],
  },
  {
    featureType: 'administrative',
    elementType: 'geometry',
    stylers: [{ color: mapStylerColor(palette.background, '#f5f5f5') }],
  },
  {
    elementType: 'labels.icon',
    stylers: [{ visibility: 'off' }],
  },
  {
    elementType: 'labels.text.fill',
    stylers: [{ color: mapStylerColor(palette.textPrimary, '#616161') }],
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [{ color: mapStylerColor(palette.background, '#ffffff') }],
  },
  {
    featureType: 'administrative.land_parcel',
    elementType: 'geometry',
    stylers: [{ color: mapStylerColor(palette.surfaceSubtle, '#ebebeb') }],
  },
  {
    featureType: 'administrative.land_parcel',
    elementType: 'labels.text.fill',
    stylers: [{ color: mapStylerColor(palette.landParcelText, '#616161') }],
  },
  {
    featureType: 'landscape.man_made',
    elementType: 'geometry',
    stylers: [{ color: mapStylerColor(palette.surfaceMuted, '#e0e0e0') }],
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [{ color: mapStylerColor(palette.poi, '#ebebeb') }],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [{ color: mapStylerColor(palette.poiText, '#757575') }],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: mapStylerColor(palette.park, '#e0e0e0') }],
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [{ color: mapStylerColor(palette.parkText, '#9e9e9e') }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: mapStylerColor(palette.road, '#ffffff') }],
  },
  {
    featureType: 'road.arterial',
    elementType: 'labels.text.fill',
    stylers: [{ color: mapStylerColor(palette.textPrimary, '#616161') }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: mapStylerColor(palette.roadHighway, '#dadada') }],
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [{ color: mapStylerColor(palette.textPrimary, '#616161') }],
  },
  {
    featureType: 'road.local',
    elementType: 'labels.text.fill',
    stylers: [{ color: mapStylerColor(palette.textSecondary, '#9e9e9e') }],
  },
  {
    featureType: 'transit.line',
    elementType: 'geometry',
    stylers: [{ color: mapStylerColor(palette.transit, '#e5e5e5') }],
  },
  {
    featureType: 'transit.station',
    elementType: 'geometry',
    stylers: [{ color: mapStylerColor(palette.transitStation, '#eeeeee') }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: mapStylerColor(palette.water, '#c9e2f5') }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: mapStylerColor(palette.textSecondary, '#9e9e9e') }],
  },
];

export const getMapStyle = (value: string | null | undefined): MapStyleTokens =>
  MAP_STYLE_VALUES.includes(value as MapStyleTokens)
    ? (value as MapStyleTokens)
    : 'brand';

/** Escape text for safe injection into InfoWindow HTML. */
export const escapeHtml = (str: string): string =>
  str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
